import { observer } from "mobx-react";
import React from "react";
import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { getModernPageColors } from "../../../../../styles/modernListingPageSx";
import { teamPermissionLabel } from "../../../../../models/Employees/TeamPermission";
import { TeamPermissionStore } from "../../../stores/TeamPermissionStore";

type TeamPermissionMatrixProps = {
  store: TeamPermissionStore;
};

const compactCheckboxSx = (
  colors: ReturnType<typeof getModernPageColors>,
  checkedColor: string,
  uncheckedColor?: string,
) => ({
  p: 0.25,
  color: uncheckedColor ?? colors.subtitle,
  "& .MuiSvgIcon-root": { fontSize: 20 },
  "&.Mui-checked, &.MuiCheckbox-indeterminate": {
    color: checkedColor,
  },
});

const TeamPermissionMatrix = ({ store }: TeamPermissionMatrixProps) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const savedCheckColor = theme.palette.customColors?.successGreen ?? colors.success;
  const newCheckColor = theme.palette.customColors?.purple ?? colors.accent;
  const removedCheckColor = theme.palette.customColors?.errorRed ?? colors.error;
  const teams = store.visibleTeams();
  const permissions = store.visiblePermissions();

  const roleIds = permissions.map((permission) => permission.id);
  const unlockedRoleIds = roleIds.filter((roleId) => store.isRoleSelected(roleId));
  const allRowsUnlocked =
    roleIds.length > 0 && unlockedRoleIds.length === roleIds.length;
  const someRowsUnlocked = unlockedRoleIds.length > 0 && !allRowsUnlocked;

  if (permissions.length === 0 || teams.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <CustomTypography variant="heading6" color={colors.title}>
          Nothing matches these filters
        </CustomTypography>
        <CustomTypography variant="body2" color={colors.subtitle}>
          Clear a team or permission filter to see the full list again.
        </CustomTypography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        maxHeight: "70vh",
        overflow: "auto",
        "&::-webkit-scrollbar": { height: 10, width: 10 },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: colors.border,
          borderRadius: 8,
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: colors.headerBg,
        },
      }}
    >
      <Table
        size="small"
        stickyHeader
        sx={{
          width: { xs: "max-content", md: "100%" },
          minWidth: { xs: "max-content", md: "100%" },
          tableLayout: { xs: "auto", md: "fixed" },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                position: "sticky",
                left: 0,
                zIndex: 3,
                bgcolor: colors.headerBg,
                width: { xs: "auto", md: 240 },
                whiteSpace: "nowrap",
                py: 0.75,
                px: 1,
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Checkbox
                  size="small"
                  checked={allRowsUnlocked}
                  indeterminate={someRowsUnlocked}
                  disabled={store.saving || roleIds.length === 0}
                  onChange={() => store.setRolesSelected(roleIds, !allRowsUnlocked)}
                  inputProps={{ "aria-label": "Unlock all visible permissions" }}
                  sx={compactCheckboxSx(colors, colors.accent)}
                />
                <CustomTypography variant="heading9" color={colors.title}>
                  Permission
                </CustomTypography>
              </Box>
            </TableCell>
            {teams.map((team) => {
              const columnRoleIds =
                unlockedRoleIds.length > 0 ? unlockedRoleIds : [];
              const column = store.columnState(team.id, columnRoleIds);
              const columnHasNewCheck = columnRoleIds.some(
                (roleId) =>
                  store.isIncluded(roleId, team.id) && !store.isSaved(roleId, team.id),
              );
              const columnHasRemoved = columnRoleIds.some(
                (roleId) =>
                  !store.isIncluded(roleId, team.id) && store.isSaved(roleId, team.id),
              );
              return (
                <TableCell
                  key={team.id}
                  align="center"
                  sx={{
                    bgcolor: colors.headerBg,
                    whiteSpace: "nowrap",
                    py: 0.75,
                    px: 0.75,
                    borderBottom: `1px solid ${colors.border}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={column === "all" && columnRoleIds.length > 0}
                      indeterminate={column === "some"}
                      disabled={store.saving || columnRoleIds.length === 0}
                      onChange={() =>
                        store.setColumn(team.id, columnRoleIds, column !== "all")
                      }
                      inputProps={{
                        "aria-label": `Select all visible permissions for ${teamPermissionLabel(team.name)}`,
                      }}
                      sx={compactCheckboxSx(
                        colors,
                        columnHasNewCheck ? newCheckColor : savedCheckColor,
                        columnHasRemoved ? removedCheckColor : undefined,
                      )}
                    />
                    <CustomTypography variant="heading9" color={colors.title}>
                      {teamPermissionLabel(team.name)}
                    </CustomTypography>
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {permissions.map((permission) => {
            const rowUnlocked = store.isRoleSelected(permission.id);
            return (
            <TableRow key={permission.id} hover>
              <TableCell
                sx={{
                  position: "sticky",
                  left: 0,
                  zIndex: 1,
                  width: { xs: "auto", md: 240 },
                  py: 0.5,
                  px: 1,
                  whiteSpace: "nowrap",
                  bgcolor: rowUnlocked ? colors.surface : colors.headerBg,
                  borderBottom: `1px solid ${colors.borderSoft}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Checkbox
                    size="small"
                    checked={rowUnlocked}
                    disabled={store.saving}
                    onChange={() => store.setRoleSelected(permission.id, !rowUnlocked)}
                    inputProps={{
                      "aria-label": `Unlock ${permission.name}`,
                    }}
                    sx={compactCheckboxSx(colors, colors.accent)}
                  />
                  <CustomTypography
                    variant="body3"
                    color={colors.title}
                    fontWeight={600}
                    style={{ opacity: rowUnlocked ? 1 : 0.7 }}
                  >
                    {permission.name}
                  </CustomTypography>
                </Box>
              </TableCell>
              {teams.map((team) => {
                const included = store.isIncluded(permission.id, team.id);
                const saved = store.isSaved(permission.id, team.id);
                const newlyChecked = included && !saved;
                const removed = !included && saved;
                return (
                  <TableCell
                    key={`${permission.id}-${team.id}`}
                    align="center"
                    sx={{
                      py: 0.75,
                      px: 0.75,
                      borderBottom: `1px solid ${colors.borderSoft}`,
                      bgcolor: rowUnlocked ? colors.surface : colors.headerBg,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: rowUnlocked ? 1 : 0.55,
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={included}
                        disabled={store.saving || !rowUnlocked}
                        onChange={() => store.toggle(permission.id, team.id)}
                        inputProps={{
                          "aria-label": `${permission.name} for ${teamPermissionLabel(team.name)}`,
                        }}
                        sx={compactCheckboxSx(
                          colors,
                          newlyChecked ? newCheckColor : savedCheckColor,
                          removed ? removedCheckColor : undefined,
                        )}
                      />
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export default observer(TeamPermissionMatrix);
